import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { getAppLocale } from "@/i18n";
import { MARKETING_ORIGIN, upsertLink, upsertMeta } from "./document-meta";

type MarketingMetaKey =
  "documentTitle" | "equipDocumentTitle" | "metaDescription" | "equipMetaDescription";

/**
 * Title, description and canonical for the two public pages.
 * Static tags in index.html cover crawlers that do not run JavaScript.
 * This updates them when the visitor switches language.
 */
export function useMarketingPageMeta(
  titleKey: Extract<MarketingMetaKey, "documentTitle" | "equipDocumentTitle">,
  descriptionKey: Extract<MarketingMetaKey, "metaDescription" | "equipMetaDescription">,
  path: "/" | "/equipos",
): void {
  const { t, i18n } = useTranslation("marketing");

  useEffect(() => {
    const title = t(titleKey);
    const description = t(descriptionKey);
    const locale = getAppLocale();
    const url = path === "/" ? `${MARKETING_ORIGIN}/` : `${MARKETING_ORIGIN}${path}`;

    document.title = title;
    document.documentElement.lang = locale;
    upsertMeta("name", "description", description);
    upsertMeta("name", "robots", "index, follow");
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:locale", locale === "es-AR" ? "es_AR" : "en_GB");
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
    upsertLink("canonical", url);
  }, [descriptionKey, i18n.language, path, t, titleKey]);
}
